import { useEffect } from "react";
import { api } from "@/lib/api";

export const SEO_PAGES = [
  { key: "home", label: "Home" },
  { key: "projects", label: "Projects" },
  { key: "services", label: "Services" },
  { key: "gallery", label: "Gallery" },
  { key: "contact", label: "Contact" },
];

export const DEFAULT_SEO = {
  share_image: "https://images.unsplash.com/photo-1530877872966-40bb5529f558?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  pages: {
    home: {
      title: "VG Homes | Premium Real Estate & Infra in Tamil Nadu for NRIs",
      description: "Premium homes & infra projects in Chennai and Tamil Nadu's metro cities, with transparent end-to-end support and live progress updates for NRI investors.",
      keywords: "NRI real estate Tamil Nadu, NRI property investment Chennai, real estate developer Chennai, premium apartments Chennai, infrastructure developer Tamil Nadu, property in Coimbatore for NRI, Madurai real estate, Trichy real estate, villas Tamil Nadu, VG Homes",
    },
    projects: {
      title: "Residential Projects in Chennai & Tamil Nadu | VG Homes",
      description: "Explore ongoing, completed and upcoming VG Homes projects — premium apartments and residences in Chennai and Tamil Nadu, ideal for NRI buyers and investors.",
      keywords: "new projects Chennai, ongoing apartments Chennai, ready to move flats Chennai, NRI apartments Tamil Nadu, upcoming projects Tamil Nadu, premium residences Chennai, VG Homes projects",
    },
    services: {
      title: "Real Estate, Infra & NRI Property Services in Tamil Nadu | VG Homes",
      description: "Real estate development, joint ventures, property management and dedicated NRI advisory across Chennai and Tamil Nadu — one accountable partner end to end.",
      keywords: "NRI property management Chennai, joint venture land development Chennai, real estate promoter Tamil Nadu, infra services Tamil Nadu, NRI home buying assistance, property management for NRIs, construction company Chennai",
    },
    gallery: {
      title: "Project Gallery – Construction Photos & Videos | VG Homes",
      description: "See construction progress, finished interiors and completed homes from VG Homes projects across Chennai and Tamil Nadu.",
      keywords: "VG Homes gallery, Chennai construction progress, apartment interiors Chennai, project photos Tamil Nadu, completed homes Chennai",
    },
    contact: {
      title: "Contact VG Homes | NRI Real Estate Advisory in Chennai, Tamil Nadu",
      description: "Talk to VG Homes about buying, investing or developing property in Chennai and Tamil Nadu. NRI-friendly support by phone, WhatsApp and email.",
      keywords: "contact VG Homes, NRI real estate consultant Chennai, property enquiry Tamil Nadu, real estate WhatsApp Chennai, NRI property advisor",
    },
  },
};

let cache = null;
const loadSeo = () => {
  if (!cache) cache = api.get("/content/seo").then((r) => r.data || {}).catch(() => ({}));
  return cache;
};

const filled = (o) => Object.fromEntries(Object.entries(o || {}).filter(([, v]) => v && String(v).trim()));

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function applySeo({ title, description, keywords, image, noindex }) {
  const url = window.location.origin + window.location.pathname + window.location.search;
  document.title = title;
  setMeta("name", "description", description);
  setMeta("name", "keywords", keywords);
  setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:type", "website");
  setMeta("property", "og:url", url);
  setMeta("property", "og:image", image);
  setMeta("property", "og:site_name", "VG Homes");
  setMeta("property", "og:locale", "en_IN");
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);
  setMeta("name", "twitter:image", image);
  setCanonical(url);
}

export function useSeo(pageKey, override) {
  const dep = JSON.stringify(override || {});
  useEffect(() => {
    let alive = true;
    loadSeo().then((saved) => {
      if (!alive) { return; }
      const base = { ...DEFAULT_SEO.pages[pageKey], ...filled(saved.pages?.[pageKey]) };
      applySeo({ ...base, image: saved.share_image || DEFAULT_SEO.share_image, ...filled(override) });
    });
    return () => { alive = false; };
  }, [pageKey, dep]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useNoIndex(title) {
  useEffect(() => {
    applySeo({ title, description: "", keywords: "", image: "", noindex: true });
  }, [title]);
}
